import type {
  DecisionStatusEnum,
  MultilegReportingTypeEnum,
  OMSOrderPropertyEnum,
  OrdStatusEnum,
  ResolutionEnum,
} from './types';

export type ResolutionEnumNoPrefix = ResolutionEnum extends `Resolution_${infer Suffix}` ? Suffix : never;

export class OrderAnalytic {
  Timestamp!: string;

  Symbol?: string;

  OrderID?: string;

  OrdStatus?: OrdStatusEnum;

  OrderQty?: string;

  Price?: string;

  ExpectedPx?: string;

  ExpectedQty?: string;

  LeavesQty?: string;

  CumQty?: string;

  AvgPx!: string;

  CumFee?: string;

  DecisionStatus?: DecisionStatusEnum;

  StartTime?: string;

  EndTime!: string;

  Markets?: any[];

  Resolution!: ResolutionEnumNoPrefix;

  CumulativeVWAP!: string;

  CumulativeTWAP!: string;

  ArrivalPx!: string;

  MarketMidPx!: string;

  ReferencePx!: string;

  TradedAvgPx!: string;

  TradedAmt!: string;

  TradedQty!: string;

  TradeCount!: number;

  CrossingFilledQty!: string;

  ParticipationRate!: string;

  GlobalParticipationRate!: string;

  EffectiveRate!: string;

  LegIndex?: number;

  Currency?: string;

  MultilegReportingType?: MultilegReportingTypeEnum;

  /**
   * Upper bound, if this is a TWAP order
   */
  UpperBound?: string;

  /**
   * Lower bound, if this is a TWAP order
   */
  LowerBound?: string;

  /**
   * Optional sweep bound price for execution analytics
   */
  SweepBound?: string;

  /**
   * Optional snipe bound price for execution analytics
   */
  SnipeBound?: string;

  /**
   * Optional snipe bound in basis points (from analytics; same bucket as SnipeBound when present)
   */
  SnipeBoundBps?: string;

  /**
   * Optional sweep bound in basis points (from analytics; same bucket as SweepBound when present)
   */
  SweepBoundBps?: string;

  /** Total quantity traded under IWould logic */
  IWouldFilledQty?: string;

  /** Volume-weighted average fill price of IWould fills */
  IWouldAvgPrice?: string;

  /** Volume-weighted average price improvement against exchange of IWould fills */
  IWouldPriceImprovementBps?: string;

  TradePredictions?: Array<{
    Property: OMSOrderPropertyEnum;
    Value: string;
  }>;

  // Readonly map of TradePredictions for direct access
  readonly tradePredictionsMap: ReadonlyMap<string, string>;

  constructor(data: Partial<OrderAnalytic>) {
    Object.assign(this, structuredClone(data));

    // Build the readonly TradePredictions map
    this.tradePredictionsMap = new Map(
      (data.TradePredictions || []).map(tradePrediction => [tradePrediction.Property, tradePrediction.Value])
    );
  }

  /**
   * Get a specific TradePrediction value by property name
   */
  getTradePrediction(property: OMSOrderPropertyEnum): string | undefined {
    return this.tradePredictionsMap.get(property);
  }

  /**
   * Check if a specific TradePrediction property exists
   */
  hasTradePrediction(property: OMSOrderPropertyEnum): boolean {
    return this.tradePredictionsMap.has(property);
  }

  /**
   * Get all TradePrediction properties as an array
   */
  getTradePredictionProperties(): OMSOrderPropertyEnum[] {
    return Array.from(this.tradePredictionsMap.keys()) as OMSOrderPropertyEnum[];
  }

  /**
   * Get the number of TradePredictions
   */
  get tradePredictionsCount(): number {
    return this.tradePredictionsMap.size;
  }

  /**
   * Check if there are any TradePredictions
   */
  get hasTradePredictions(): boolean {
    return this.tradePredictionsCount > 0;
  }
}
